import { type FixedDepartureDetail } from '@utazzo/shared';
import { UzAlert, UzButton, UzDatePicker, UzMoneyInput, UzSwitch } from '@utazzo/ui';
import { CalendarDays, Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import React, { useState } from 'react';

import {
  useCreateFixedDepartureDepartureDate,
  useDeleteFixedDepartureDepartureDate,
  useUpdateFixedDepartureDepartureDate,
} from '../use-fixed-departures';

import { formatDMY } from '@/lib/date-format';

const dateToYmd = (d: Date | null | undefined): string =>
  d ? new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10) : '';

const fmtDate = (ymd: string): string => formatDMY(ymd);

/**
 * Scheduled departure dates (optional).
 *
 * The whole point is the empty state: a template with no dates runs every day,
 * and the agent picks any start (Kashmir). Add even one date and the template
 * becomes date-locked - itineraries must then start on one of these, enforced
 * both in the picker and on the server.
 *
 * The per-date surcharge is a peak-season top-up added to the land price for
 * that start date. It may be NEGATIVE - an off-season discount that is
 * subtracted for that date (the server adds it to the land total as-is).
 */
export const FdDeparturesStep = ({ fd }: { fd: FixedDepartureDetail }): React.JSX.Element => {
  const createDate = useCreateFixedDepartureDepartureDate(fd.id);
  const updateDate = useUpdateFixedDepartureDepartureDate(fd.id);
  const deleteDate = useDeleteFixedDepartureDepartureDate(fd.id);

  const [newDate, setNewDate] = useState('');
  const [newSurcharge, setNewSurcharge] = useState(0);

  // Inline edit of an existing row (date + surcharge).
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDate, setEditDate] = useState('');
  const [editSurcharge, setEditSurcharge] = useState(0);

  const currency = fd.currencyCode ?? 'INR';

  const add = async (): Promise<void> => {
    if (!newDate) return;
    await createDate.mutateAsync({
      departureDate: newDate,
      surchargePaise: String(Math.round(newSurcharge * 100)),
    });
    setNewDate('');
    setNewSurcharge(0);
  };

  const startEdit = (d: FixedDepartureDetail['departureDates'][number]): void => {
    setEditingId(d.id);
    setEditDate(d.departureDate);
    setEditSurcharge(Number(d.surchargePaise) / 100);
  };

  const cancelEdit = (): void => {
    setEditingId(null);
    setEditDate('');
    setEditSurcharge(0);
  };

  const saveEdit = async (): Promise<void> => {
    if (!editingId || !editDate) return;
    await updateDate.mutateAsync({
      dateId: editingId,
      payload: {
        departureDate: editDate,
        surchargePaise: String(Math.round(editSurcharge * 100)),
      },
    });
    cancelEdit();
  };

  // "+ USD 500 / person" for a surcharge, "- USD 500 / person (discount)" for a
  // negative one - the surcharge is charged per paying traveller.
  const surchargeLabel = (paise: string): string => {
    const amount = Number(paise) / 100;
    const abs = Math.abs(amount).toLocaleString('en-IN');
    return amount < 0
      ? `- ${currency} ${abs} / person (discount)`
      : `+ ${currency} ${abs} / person`;
  };

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-zinc-200 bg-white p-5">
        <h2 className="text-sm font-semibold text-zinc-900">Scheduled departures</h2>
        <p className="mt-0.5 text-xs text-zinc-500">
          Leave empty for an every-day package - the agent then picks any start date. Add dates to
          lock this fixed departure to set departures. A surcharge tops up that date&apos;s land
          price <strong>per person</strong> (it scales with the party); enter a negative amount to
          give a per-person discount for that date instead.
        </p>

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <UzDatePicker
            label="Departure date"
            displayFormat="dd-MM-yyyy"
            placeholder="Add a departure"
            value={newDate ? new Date(`${newDate}T00:00:00.000Z`) : undefined}
            onValueChange={(d) => setNewDate(dateToYmd(d))}
          />
          <UzMoneyInput
            label={`Surcharge / discount per person (${currency})`}
            currency={currency}
            allowNegative
            value={newSurcharge}
            onValueChange={(v) => setNewSurcharge(v ?? 0)}
          />
          <UzButton onClick={add} disabled={!newDate} loading={createDate.isPending}>
            <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            Add departure
          </UzButton>
        </div>

        {fd.departureDates.length === 0 ? (
          <UzAlert tone="info" className="mt-5">
            <span className="flex items-start gap-2">
              <CalendarDays className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>
                No scheduled departures - this fixed departure runs on any date the agent chooses.
              </span>
            </span>
          </UzAlert>
        ) : (
          <div className="mt-5 space-y-2">
            {fd.departureDates.map((d) => {
              const isEditing = editingId === d.id;
              if (isEditing) {
                return (
                  <div
                    key={d.id}
                    className="border-primary/40 bg-primary/5 flex flex-wrap items-end gap-3 rounded-lg border px-4 py-3"
                  >
                    <div>
                      <p className="mb-1 text-xs font-medium text-zinc-700">Departure date</p>
                      <UzDatePicker
                        displayFormat="dd-MM-yyyy"
                        value={editDate ? new Date(`${editDate}T00:00:00.000Z`) : undefined}
                        onValueChange={(dt) => setEditDate(dateToYmd(dt))}
                      />
                    </div>
                    <UzMoneyInput
                      label={`Surcharge / discount per person (${currency})`}
                      currency={currency}
                      allowNegative
                      value={editSurcharge}
                      onValueChange={(v) => setEditSurcharge(v ?? 0)}
                    />
                    <UzButton
                      size="sm"
                      onClick={saveEdit}
                      disabled={!editDate}
                      loading={updateDate.isPending}
                    >
                      <Check className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                      Save
                    </UzButton>
                    <UzButton size="sm" variant="ghost" onClick={cancelEdit}>
                      <X className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                      Cancel
                    </UzButton>
                  </div>
                );
              }
              return (
                <div
                  key={d.id}
                  className="flex items-center gap-4 rounded-lg border border-zinc-200 px-4 py-2.5"
                >
                  <span className="flex-1 text-sm font-medium text-zinc-900">
                    {fmtDate(d.departureDate)}
                  </span>
                  {Number(d.surchargePaise) !== 0 && (
                    <span
                      className={
                        'text-xs ' +
                        (Number(d.surchargePaise) < 0 ? 'text-emerald-600' : 'text-zinc-500')
                      }
                    >
                      {surchargeLabel(d.surchargePaise)}
                    </span>
                  )}
                  <label className="flex items-center gap-1.5 text-xs text-zinc-600">
                    <UzSwitch
                      checked={d.isActive}
                      onCheckedChange={(v) =>
                        updateDate.mutate({ dateId: d.id, payload: { isActive: v } })
                      }
                    />
                    {d.isActive ? 'Open' : 'Closed'}
                  </label>
                  <UzButton
                    variant="ghost"
                    size="icon"
                    className="hover:text-primary h-8 w-8 shrink-0 text-zinc-400"
                    onClick={() => startEdit(d)}
                    aria-label="Edit departure date"
                  >
                    <Pencil className="h-4 w-4" aria-hidden />
                  </UzButton>
                  <UzButton
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-zinc-400 hover:text-red-600"
                    onClick={() => deleteDate.mutate(d.id)}
                    aria-label="Remove departure date"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </UzButton>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};
